import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSearch, SearchResult } from '../../contexts/SearchContext';
import { safeJsonParse } from '../../utils/securityUtils';
import { trackSearch, trackAddToCart } from '../../utils/analytics';
import OptimizedImage from '../OptimizedImage';
import './SearchResults.css';

const SearchResults: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { searchResults, searchQuery, isSearching, performSearch } = useSearch();
  const lastUrlQuery = useRef<string>('');
  
  const urlQuery = searchParams.get('q') || '';

  // Only perform search when URL query changes, don't interfere with real-time search
  useEffect(() => {
    if (urlQuery && urlQuery !== lastUrlQuery.current) {
      lastUrlQuery.current = urlQuery;
      performSearch(urlQuery);
      // GA4 search event — result count available after next render,
      // so we pass what we have; searchResults updates asynchronously
      trackSearch(urlQuery);
    }
  }, [urlQuery, performSearch]);

  // Once results arrive, fire search again with the count so GA has accurate data
  useEffect(() => {
    if (urlQuery && !isSearching && searchResults.length >= 0) {
      trackSearch(urlQuery, searchResults.length);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults.length, isSearching]);

  // Use URL query for display, but don't override the search context
  const displayQuery = urlQuery;
  const displayResults = searchResults;

  const handleResultClick = (result: SearchResult) => {
    if (result.type === 'service') {
      navigate(`/service/${result.id}`);
    } else if (result.type === 'subservice') {
      // Navigate to the parent service page with subservice highlighted
      navigate(`/service/${result.serviceId}`, { 
        state: { highlightSubService: result.id } 
      });
    }
  };

  const addToCart = (result: SearchResult, event: React.MouseEvent) => {
    event.stopPropagation();
    
    if (result.type === 'subservice') {
      const cartItem = {
        id: result.id,
        name: result.name,
        price: result.price,
        image: result.image,
        serviceId: result.serviceId
      };
      
      const existingCart: any[] = safeJsonParse(localStorage.getItem('cart'), []);
      const existingItemIndex = existingCart.findIndex((item: any) => item.id === result.id);
      
      if (existingItemIndex > -1) {
        existingCart[existingItemIndex].quantity = (existingCart[existingItemIndex].quantity || 1) + 1;
      } else {
        existingCart.push({ ...cartItem, quantity: 1 });
      }
      
      localStorage.setItem('cart', JSON.stringify(existingCart));
      window.dispatchEvent(new CustomEvent('cartUpdated'));

      // GA4 add_to_cart
      const numericPrice = parseFloat((result.price || '0').replace(/[^0-9.]/g, '')) || 0;
      trackAddToCart({
        item_id: String(result.id),
        item_name: result.name,
        item_category: 'Puja Service',
        price: numericPrice,
        quantity: 1,
      });

      // Show success message
      alert(`${result.name} added to cart!`);
    }
  };

  const addToWishlist = (result: SearchResult, event: React.MouseEvent) => {
    event.stopPropagation();
    
    if (result.type === 'subservice') {
      const wishlistItem = {
        id: result.id,
        name: result.name,
        price: result.price,
        image: result.image,
        serviceId: result.serviceId
      };
      
      const existingWishlist: any[] = safeJsonParse(localStorage.getItem('wishlist'), []);
      const existingItemIndex = existingWishlist.findIndex((item: any) => item.id === result.id);
      
      if (existingItemIndex === -1) {
        existingWishlist.push(wishlistItem);
        localStorage.setItem('wishlist', JSON.stringify(existingWishlist));
        window.dispatchEvent(new CustomEvent('wishlistUpdated'));
        alert(`${result.name} added to wishlist!`);
      } else {
        alert(`${result.name} is already in your wishlist!`);
      }
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={`star ${i < rating ? 'filled' : ''}`}>
        ⭐
      </span>
    ));
  };

  if (isSearching) {
    return (
      <div className="search-results-container">
        <div className="search-header">
          <h1>Searching...</h1>
        </div>
        <div className="search-loading">
          <div className="spinner"></div>
          <p>Finding spiritual services for you...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="search-results-container">
      <div className="search-header">
        <h1>Search Results</h1>
        {displayQuery && (
          <p className="search-query">
            {displayResults.length > 0 ? (
              <>Found <strong>{displayResults.length}</strong> result{displayResults.length !== 1 ? 's' : ''} for "<strong>{displayQuery}</strong>"</>
            ) : (
              <>No results found for "<strong>{displayQuery}</strong>"</>
            )}
          </p>
        )}
      </div>

      {displayResults.length === 0 && displayQuery ? (
        <div className="no-results">
          <div className="no-results-icon">🔍</div>
          <h2>No services found</h2>
          <p>Try searching for:</p>
          <ul className="search-suggestions">
            <li>Maha Mrityunjaya</li>
            <li>Rudrabhishekh</li>
            <li>Puja services</li>
            <li>Spiritual guidance</li>
            <li>Hanuman Chalisa</li>
            <li>Ganesha worship</li>
          </ul>
        </div>
      ) : (
        <div className="search-results-grid">
          {displayResults.map((result) => (
            <div
              key={`${result.type}-${result.id}`}
              className={`search-result-card ${result.type}`}
              onClick={() => handleResultClick(result)}
            >
              <div className="result-image">
                <OptimizedImage src={result.image} alt={result.name} />
                <div className="result-type-badge">
                  {result.type === 'service' ? 'Service Category' : 'Puja Service'}
                </div>
              </div>
              
              <div className="result-content">
                <h3 className="result-title">{result.name}</h3>
                <p className="result-description">{result.description}</p>
                
                <div className="result-details">
                  {result.rating && (
                    <div className="result-rating">
                      {renderStars(result.rating)}
                      <span className="rating-value">({result.rating})</span>
                    </div>
                  )}
                  
                  {result.duration && (
                    <div className="result-duration">
                      <span className="duration-icon">⏱️</span>
                      <span>{result.duration}</span>
                    </div>
                  )}
                  
                  {result.category && (
                    <div className="result-category">
                      <span className="category-icon">🏷️</span>
                      <span>{result.category}</span>
                    </div>
                  )}
                </div>
                
                <div className="result-footer">
                  <div className="result-price">
                    {result.price && <span className="price">{result.price}</span>}
                    {result.priceRange && <span className="price-range">{result.priceRange}</span>}
                  </div>
                  
                  {result.type === 'subservice' && (
                    <div className="result-actions">
                      <button
                        className="action-btn wishlist-btn"
                        onClick={(e) => addToWishlist(result, e)}
                        title="Add to Wishlist"
                      >
                        ❤️
                      </button>
                      <button
                        className="action-btn cart-btn"
                        onClick={(e) => addToCart(result, e)}
                        title="Add to Cart"
                      >
                        🛒 Add to Cart
                      </button>
                    </div>
                  )}
                  
                  {result.type === 'service' && (
                    <div className="result-actions">
                      <button className="view-services-btn">
                        View Services
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchResults;